// src/lib/swUpdater.ts
// Client/PWA updater: находит новую версию ассетов и применяет по клику.
// Канон: без смешения env, мягкий reload, безопасный search_path не нужен (клиент).

type UpdaterOptions = {
  scriptUrl?: string;
  onNeedRefresh?: (meta: { buildId?: string; createdAt?: string }) => void;
  beforeReload?: () => void;
  onEvent?: (name: string, data?: Record<string, any>) => void;
  enablePolling?: boolean;
  pollIntervalMs?: number;
};

export type SWUpdater = {
  applyUpdate: () => Promise<void>;
  getState: () => { waiting: boolean; buildId?: string; createdAt?: string };
  destroy: () => void;
};

function getEnvFromHost(host = window.location.hostname): 'stg' | 'prd' {
  if (host.startsWith('staging.')) return 'stg';
  return 'prd';
}

async function fetchBuildMeta(): Promise<{ buildId?: string; createdAt?: string } | undefined> {
  try {
    const res = await fetch('/meta/build.json', { cache: 'no-store' });
    if (!res.ok) return;
    return await res.json();
  } catch {
    return;
  }
}

export async function createSwUpdater(opts: UpdaterOptions = {}): Promise<SWUpdater | null> {
  if (!('serviceWorker' in navigator)) return null;

  const {
    scriptUrl = '/sw.js',
    onNeedRefresh,
    beforeReload,
    onEvent,
    enablePolling = true,
    pollIntervalMs = 5 * 60_000,
  } = opts;

  const currentEnv = getEnvFromHost();

  const reg = await navigator.serviceWorker
    .register(scriptUrl)
    .catch((err) => {
      onEvent?.('sw_register_error', { message: String(err) });
      return null;
    });

  if (!reg) return null;

  let waitingRegistration: ServiceWorker | null = null;
  let meta: { buildId?: string; createdAt?: string } = {};
  let pollTimer: number | undefined;

  fetchBuildMeta().then((m) => {
    if (m) meta = m;
  });

  const onUpdateFound = () => {
    const installing = reg.installing;
    if (!installing) return;
    installing.addEventListener('statechange', async () => {
      if (installing.state === 'installed' && navigator.serviceWorker.controller) {
        waitingRegistration = reg.waiting;
        onEvent?.('release_seen', { release_id: meta.buildId, env: currentEnv });
        onNeedRefresh?.(meta);
      }
    });
  };

  reg.addEventListener?.('updatefound', onUpdateFound);

  // Only enable polling in production (not in dev or staging)
  if (enablePolling && import.meta.env.PROD && import.meta.env.VITE_ENV_LABEL === 'PRD') {
    // @ts-expect-error timer
    pollTimer = window.setInterval(async () => {
      try {
        await reg.update();
        const m = await fetchBuildMeta();
        if (m && m.buildId && m.buildId !== meta.buildId) meta = m;
      } catch (e) {
        onEvent?.('sw_update_check_failed', { message: String(e) });
      }
    }, pollIntervalMs);
  }

  const applyUpdate = async () => {
    if (!waitingRegistration && reg.waiting) waitingRegistration = reg.waiting;
    if (!waitingRegistration) return;

    onEvent?.('release_cta_click', { release_id: meta.buildId, env: currentEnv });

    try {
      beforeReload?.();
    } catch {
      /* no-op */
    }

    waitingRegistration.postMessage({ type: 'SKIP_WAITING' });

    await new Promise<void>((resolve) => {
      const onControllerChange = () => {
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        resolve();
      };
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    });

    onEvent?.('release_apply_success', { release_id: meta.buildId, env: currentEnv });
    window.location.reload();
  };

  const getState = () => ({
    waiting: Boolean(waitingRegistration || reg.waiting),
    buildId: meta.buildId,
    createdAt: meta.createdAt,
  });

  const destroy = () => {
    if (pollTimer) window.clearInterval(pollTimer);
    try {
      reg.removeEventListener?.('updatefound', onUpdateFound as any);
    } catch {
      /* no-op */
    }
  };

  return { applyUpdate, getState, destroy };
}

// Legacy compatibility layer
class SWUpdater {
  registration?: ServiceWorkerRegistration;
  private updater: SWUpdater | null = null;

  async init(opts: { allowAutoReload: boolean }) {
    this.updater = await createSwUpdater({
      onNeedRefresh: (meta) => {
        window.dispatchEvent(new CustomEvent('sw-update-available', {
          detail: { buildSha: meta.buildId || 'dev' }
        }));
      },
    });
  }

  async teardown() {
    this.updater?.destroy();
  }

  async checkForUpdates() {
    await this.registration?.update();
  }

  async forceUpdate() {
    await this.updater?.applyUpdate();
  }

  reloadWithNewSW() {
    this.updater?.applyUpdate();
  }

  async getBuildSha(): Promise<string | null> {
    const state = this.updater?.getState();
    return state?.buildId || null;
  }
}

export async function initSW() {
  if (typeof window === 'undefined') return;

  const isTg = window.location.pathname.startsWith('/tg') || !!(window as any).Telegram?.WebApp;

  if (isTg) {
    console.log('[SW] Telegram environment detected - unregistering SW');
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
      console.log('[SW] hard-off active for /tg');
    }
    return;
  }

  const isProd = import.meta.env?.PROD || import.meta.env?.VITE_ENV_LABEL === 'PRD';
  if (isProd) {
    swUpdater.init({ allowAutoReload: false });
  }

  (window as any).swUpdater = swUpdater;
}

export const swUpdater = new SWUpdater();